import { AnimatePresence, motion } from "framer-motion";
import { useContext, useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import type { ConfigId } from "@/constants/ConfigData";
import { BotContext } from "@/pages/cbb-builder/store";
import useStore from "@/pages/omni-builder/store";
import { userSlice } from "@/redux/User";
import { axiosPost } from "@/services/axiosService";
import environment from "@/constants/Environment";
import { debounce } from "lodash";
import moment from "moment";
import { CompleteIcon, UploadDeleteIcon } from "@/assets/AiBotAssets";
import MiniTooltip from "@/assets/MiniTooltip";
import ThreeDot from "@/components/loaders/ThreeDot";
import useGooglePickerConfig from "@/pages/omni-builder/react-query/configs/useGooglePickerConfig";
import useNotionConfig from "@/pages/omni-builder/react-query/configs/useNotionConfig";
import useConfluenceConfig from "@/pages/omni-builder/react-query/configs/useConfluenceConfig";
import useSalesForceConfig from "@/pages/omni-builder/react-query/configs/useSalesForceConfig";
import {
  EditAppIcon,
  TrainingProgressIcon,
  IErrIconSvg,
  RetrainBtnIcon,
} from "@/pages/cbb-builder/components/AiBotAssets";
import {
  getAccounts,
  unifiedGoogleAuth,
} from "@/services/googlepicker.service";
import { ConnectIcon, connectAppGroups } from "./connectApps.config";
import RemoveConfirmation from "@/pages/cbb-builder/components/RemoveConfirmation";
import ModalWrapper from "@/pages/cbb-builder/components/ModalWrapper";

const TRAINING_DELETE_STATUSES = new Set(["In Progress", "Re-syncing"]);

function buildConnectedSourceDeleteMessage(pendingSource: any[]) {
  const hasTrainingInProgress = pendingSource.some((item) =>
    TRAINING_DELETE_STATUSES.has(item?.status ?? ""),
  );

  if (hasTrainingInProgress) {
    return `${pendingSource.length > 1 ? "Some selected documents are" : "This document is"} still training. Deleting ${pendingSource.length > 1 ? "them" : "it"} will stop training. This action cannot be undone.`;
  }

  return `Are you sure you want to delete ${pendingSource.length > 1 ? `${pendingSource.length} trained documents` : "this trained document"}? This action cannot be undone.`;
}

const trainedFilesGridClass =
  "grid !grid-cols-[280px_173px_110px_140px_110px_1fr] items-center";
const environmentData = environment();

const ConnectAppsStep = () => {
  const [store, storeDispatch] = useContext(BotContext);
  const setOpenConfigModalId = useStore((state) => state.setOpenConfigModalId);
  const dispatch = useDispatch();
  const isOpeningPicker = useRef(false);

  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);
  const [nativeAppSearch, setNativeAppSearch] = useState<{
    [key: string]: string;
  }>({});
  const [selectedItems, setSelectedItems] = useState<Map<string, Set<string>>>(
    new Map(),
  );
  const [resyncData, setResyncData] = useState<Record<string, any[]>>({});
  const [inProcess, setInProcess] = useState(false);
  const [pendingDeleteConfig, setPendingDeleteConfig] = useState<{
    title: string;
    sourceType: string;
    source: any[];
    onAfterDelete?: () => void;
  } | null>(null);
  const [removeData, setRemoveData] = useState<{
    status: boolean;
    remove: () => void;
  }>({
    status: false,
    remove: () => {},
  });

  const { data: googlePickerConfigs, isLoading: googleLoading } =
    useGooglePickerConfig();
  const { isLoading: notionLoading, data: notionConfigs } = useNotionConfig();
  const { isLoading: confluenceLoading, data: confluenceConfigs } =
    useConfluenceConfig();
  const { isLoading: salesforceLoading, data: salesforceConfigs } =
    useSalesForceConfig();

  const trainedSource = store.botData.aiSource || {};

  const Configs = {
    notion: { config: notionConfigs, loading: notionLoading },
    confluence: { config: confluenceConfigs, loading: confluenceLoading },
    salesforce: {
      config: salesforceConfigs?.length ? salesforceConfigs : null,
      loading: salesforceLoading,
    },
    googlepicker: { config: googlePickerConfigs, loading: googleLoading },
  };

  const handleOpenPicker = async () => {
    if (isOpeningPicker.current) return;
    isOpeningPicker.current = true;

    try {
      const account = await getAccounts();

      if (!account.length) {
        await unifiedGoogleAuth({
          clientId: encodeURIComponent(
            String(environmentData.PUBLIC_GOOGLE_CLIENT_ID),
          ),
          redirectUri: `${window.location.origin}/authorize/googleapis`,
          setOpenConfigModalId: (id: string) =>
            setOpenConfigModalId(id as ConfigId),
          dispatch,
        });
        return;
      }

      storeDispatch({
        type: "set-openConfigModalId",
        configId: "googlepicker",
      });
      setOpenConfigModalId("googlepicker");
    } catch (error) {
      console.error("Error during Google auth or picker open:", error);
    } finally {
      isOpeningPicker.current = false;
    }
  };

  const handleConnect = async (configId: ConfigId) => {
    if (configId === "googlepicker") {
      await handleOpenPicker();
      return;
    }

    storeDispatch({ type: "set-openConfigModalId", configId });
    setOpenConfigModalId(configId);
  };

  const deleteTrainDocument = async ({
    source,
    sourceType,
  }: {
    source: any[];
    sourceType: string;
  }) => {
    try {
      if (!source || source.length === 0)
        throw new Error("No source found to delete");
      setInProcess(true);

      const convertedArray = Array.isArray(source) ? source : [source];
      const updatedArray = convertedArray.map((item) => ({
        ...item,
        ...(item.characterCount === undefined && {
          characterCount: item.text?.length || 0,
        }),
      }));

      const res = await axiosPost("/ai/delete-content", {
        sources: updatedArray,
        size: 0,
        botCustomId: store.botData.botCustomId,
        botCreatedAt: store.botData.createdAt,
        sourceType,
      });

      const deletedIds = new Set(
        updatedArray.map(
          (item) => item.googlePickerId || item.id || item.title || item.url,
        ),
      );
      const nextSources = (store.botData.aiSource?.[sourceType] || []).filter(
        (item: any) =>
          !deletedIds.has(
            item.googlePickerId || item.id || item.title || item.url,
          ),
      );

      storeDispatch({
        type: "set-botData",
        botData: {
          ...store.botData,
          aiBotConfig: {
            ...store.botData.aiBotConfig,
            characterUsed: res.data.characterUsed,
          },
          aiSource: {
            ...store.botData.aiSource,
            [sourceType]: nextSources,
          },
        },
      });

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message:
            "Selected data has been successfully removed from the training data.",
        }),
      );
    } catch (err: any) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to delete files",
        }),
      );
    } finally {
      setInProcess(false);
    }
  };

  const handleNativeAppsResync = (currentResyncItems: any[], key: string) => {
    const curDataset: any[] = [];
    const resyncItems: any = [];
    let authId: any;
    const resyncAll = currentResyncItems?.filter(
      (d) => d.status !== "Re-syncing" && d.status !== "In Progress",
    );
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      newMap.set(key, new Set());
      return newMap;
    });
    setResyncData((prev) => ({ ...prev, [key]: [] }));
    if (resyncAll?.length === 0) return;
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: `${resyncAll.length} links added to resync.`,
      }),
    );
    for (const item of store.botData?.aiSource?.[key] || []) {
      const urlItem = resyncAll?.filter((t) => t.url === item.title);
      if (urlItem[0]) {
        authId = item.authId;
        resyncItems.push(urlItem[0].url);
        curDataset.push({
          ...item,
          status: "Re-syncing",
        });
      } else {
        curDataset.push(item);
      }
    }

    storeDispatch({
      type: "set-aiSource",
      dataKey: key,
      dataSet: curDataset,
    });

    const sourcesToProcess = ["confluence", "notion"];
    const payload = {
      links: {
        [key]: resyncItems,
      },
      botCustomId: store.botData.botCustomId,
      botCreatedAt: store.botData.createdAt,
      ...(sourcesToProcess.includes(key) && { authId }),
    };

    axiosPost("/ai/resync-multiple", payload);
  };

  const debouncedHandleNativeAppsResync = useMemo(
    () => debounce(handleNativeAppsResync, 500),
    [store.botData, dispatch, storeDispatch],
  );

  const handleAllCheckToggle = (cardKey: string) => {
    const allItems = trainedSource[cardKey] || [];
    const selectableItems = allItems.filter(
      (i: any) => i.status !== "In Progress..",
    );
    const currentCardSelected = selectedItems.get(cardKey) || new Set();
    const isAllSelected =
      currentCardSelected.size === selectableItems.length &&
      selectableItems.length > 0;

    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      if (!isAllSelected) {
        const newSet = new Set(
          selectableItems.map((i: any) => i.googlePickerId || i.id || i.title),
        );
        newMap.set(cardKey, newSet as Set<string>);
      } else {
        newMap.set(cardKey, new Set());
      }
      return newMap;
    });

    setResyncData((prevData) => {
      let updatedItems: any[];
      if (!isAllSelected) {
        updatedItems = selectableItems.map((i: any) => ({
          url: i.title,
          status: i.status,
        }));
      } else {
        updatedItems = [];
      }
      return { ...prevData, [cardKey]: updatedItems };
    });
  };

  const handleSingleItemToggle = (
    cardKey: string,
    itemId: string,
    title: string,
    status: string,
  ) => {
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      const currentCardItems = new Map(newMap).get(cardKey) || new Set();
      const nextCardItems = new Set(currentCardItems);
      if (nextCardItems.has(itemId)) {
        nextCardItems.delete(itemId);
      } else {
        nextCardItems.add(itemId);
      }
      newMap.set(cardKey, nextCardItems);
      return newMap;
    });

    setResyncData((prevData) => {
      const prevItems = prevData[cardKey] || [];
      const isAlreadyAdded = prevItems?.some((t) => t.url === title);
      let updatedItems: any[];
      if (isAlreadyAdded) {
        updatedItems = prevItems?.filter((t) => t.url !== title);
      } else {
        updatedItems = [...prevItems, { url: title, status }];
      }
      return { ...prevData, [cardKey]: updatedItems };
    });
  };

  return (
    <>
      <motion.div
        className="flex flex-col gap-[14px]"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
      >
        {connectAppGroups.map((group, groupIndex) => (
          <section className="flex flex-col gap-[8px]" key={group.title}>
            <h3 className="text-[14px] font-semibold leading-[110%] text-[#444]">
              {group.title}
            </h3>

            <div className="flex flex-col gap-[8px]">
              {group.apps.map((app, appIndex) => {
                const isOpening = store.openConfigModalId === app.key;
                const appConfig = Configs[app.key as keyof typeof Configs];
                const hasConfig = !!appConfig?.config;
                const isLoading = appConfig?.loading || isOpening;

                const allDocs = trainedSource[app.key] || [];

                const filteredDocs = allDocs;

                const currentCardSelectedItems =
                  selectedItems.get(app.key) || new Set();
                const currentResyncItems = resyncData[app.key] || [];
                const isAllSelected =
                  currentCardSelectedItems.size === filteredDocs.length &&
                  filteredDocs.length > 0;

                return (
                  <motion.div
                    key={app.key}
                    className="border-[rgba(0, 0, 0, 0.10)] flex flex-col gap-[16px] rounded-[16px] border bg-[#fafafa] p-[20px]"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.18,
                      delay: groupIndex * 0.04 + appIndex * 0.035,
                      ease: "easeOut",
                    }}
                  >
                    <div className="flex items-center justify-between gap-[18px]">
                      <div className="flex min-w-0 items-center gap-[16px]">
                        <span className="flex h-[42px] w-[42px] shrink-0 items-center justify-center">
                          {app.icon}
                        </span>
                        <div className="min-w-0">
                          <h4 className="mb-[4px] truncate text-[14px] font-semibold leading-[120%] text-[#444]">
                            {app.title}
                          </h4>
                          <p className="truncate text-[13px] font-normal leading-[130%] text-[#666]">
                            {app.subtitle}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-[12px]">
                        {hasConfig ? (
                          <motion.button
                            className="flex h-[34px] shrink-0 items-center justify-center gap-[6px] rounded-[10px] border border-[rgba(0,0,0,0.1)] bg-white px-[17px] text-[13px] font-medium leading-[120%] text-[#444] transition-colors hover:bg-[#f5f5f5]"
                            type="button"
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleConnect(app.key)}
                          >
                            <EditAppIcon />
                            Settings
                          </motion.button>
                        ) : (
                          <motion.button
                            className="flex h-[34px] shrink-0 items-center justify-center gap-[6px] rounded-[10px] border border-[#FFA2A7] bg-white px-[17px] text-[13px] font-medium leading-[120%] text-[#FB5F66] transition-colors hover:bg-[#fff5f6]"
                            type="button"
                            whileTap={{ scale: 0.97 }}
                            disabled={isLoading}
                            onClick={() => handleConnect(app.key)}
                          >
                            {isLoading ? (
                              <div className="flex items-center gap-2">
                                Loading <ThreeDot />
                              </div>
                            ) : (
                              <>
                                <ConnectIcon />
                                Connect
                              </>
                            )}
                          </motion.button>
                        )}
                      </div>
                    </div>

                    {/* Imported Documents Section */}
                    {allDocs.length > 0 && (
                      <div className="flex flex-col gap-[12px] border-t border-[rgba(0,0,0,0.05)] pt-[8px]">
                        <div className="flex items-center justify-between">
                          <h3
                            className="flex cursor-pointer items-center gap-[8px] text-[14px] font-semibold leading-[110%] text-[#444]"
                            onClick={() =>
                              setExpandedIndex(
                                expandedIndex === app.key ? null : app.key,
                              )
                            }
                          >
                            <svg
                              className={`transition-transform ${expandedIndex === app.key ? "" : "-rotate-90"}`}
                              xmlns="http://www.w3.org/2000/svg"
                              width="11"
                              height="11"
                              viewBox="0 0 11 11"
                              fill="none"
                            >
                              <path
                                d="M0.483887 2.31771L5.69222 7.73438L10.9006 2.31771L0.483887 2.31771Z"
                                fill="#444444"
                              />
                            </svg>
                            Imported Documents
                          </h3>

                          <AnimatePresence>
                            {expandedIndex === app.key &&
                              currentCardSelectedItems.size > 0 && (
                                <motion.div
                                  initial={{ opacity: 0, x: 10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 10 }}
                                  className="flex items-center gap-[12px]"
                                >
                                  <button
                                    type="button"
                                    disabled={inProcess}
                                    onClick={() => {
                                      const selected = allDocs.filter(
                                        (i: any) =>
                                          currentCardSelectedItems.has(
                                            i.googlePickerId || i.id || i.title,
                                          ),
                                      );
                                      setPendingDeleteConfig({
                                        title: app.title,
                                        sourceType: app.key,
                                        source: selected,
                                        onAfterDelete: () => {
                                          setSelectedItems((prev) => {
                                            const n = new Map(prev);
                                            n.set(app.key, new Set());
                                            return n;
                                          });
                                        },
                                      });
                                      setRemoveData({
                                        status: true,
                                        remove: async () => {
                                          await deleteTrainDocument({
                                            source: selected,
                                            sourceType: app.key,
                                          });
                                          setSelectedItems((prev) => {
                                            const n = new Map(prev);
                                            n.set(app.key, new Set());
                                            return n;
                                          });
                                          setPendingDeleteConfig(null);
                                          setRemoveData((prev) => ({
                                            ...prev,
                                            status: false,
                                          }));
                                        },
                                      });
                                    }}
                                    className={`flex items-center gap-[4px] text-[13px] font-medium text-[#fb5f66] ${inProcess ? "opacity-50" : "hover:text-[#e0444b]"}`}
                                  >
                                    <UploadDeleteIcon />
                                    Delete Selected
                                  </button>
                                </motion.div>
                              )}
                          </AnimatePresence>
                        </div>

                        <AnimatePresence>
                          {expandedIndex === app.key && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden rounded-[18px] border border-[rgba(0,0,0,0.10)] bg-white"
                            >
                              <div className="new-scrollbar overflow-x-auto overflow-y-hidden">
                                <div className="w-full min-w-[900px]">
                                  <div
                                    className={`${trainedFilesGridClass} min-h-[46px] border-b border-[rgba(0,0,0,0.08)] bg-[#fafafa] text-[13px] font-semibold leading-[110%] text-[#444]`}
                                  >
                                    <div className="flex h-full items-center gap-[14px] px-[20px]">
                                      <input
                                        checked={isAllSelected}
                                        onChange={() =>
                                          handleAllCheckToggle(app.key)
                                        }
                                        className="onboarding-custom-checkbox"
                                        type="checkbox"
                                      />
                                      <span>Document Name</span>
                                    </div>
                                    <div className="flex h-full items-center border-l border-[rgba(0,0,0,0.08)] px-[20px]">
                                      Status
                                    </div>
                                    <div className="flex h-full items-center border-l border-[rgba(0,0,0,0.08)] px-[20px]">
                                      Characters
                                    </div>
                                    <div className="flex h-full items-center border-l border-[rgba(0,0,0,0.08)] px-[20px]">
                                      Last Updated
                                    </div>
                                    <div className="flex h-full items-center justify-center border-l border-[rgba(0,0,0,0.08)] px-[20px]">
                                      Action
                                    </div>
                                  </div>

                                  <div className="new-scrollbar max-h-[300px] overflow-y-auto">
                                    {filteredDocs.map(
                                      (doc: any, docIdx: number) => {
                                        const docId =
                                          doc.googlePickerId ||
                                          doc.id ||
                                          doc.title;
                                        const isSelected =
                                          currentCardSelectedItems.has(docId);

                                        return (
                                          <div
                                            key={docId + docIdx}
                                            className={`${trainedFilesGridClass} min-h-[56px] border-b border-[rgba(0,0,0,0.08)] text-[13px] leading-[130%] text-[#444] last:border-b-0 ${inProcess ? "pointer-events-none opacity-50" : ""}`}
                                          >
                                            <div className="flex min-w-0 items-center gap-[16px] px-[20px]">
                                              <input
                                                checked={isSelected}
                                                onChange={() =>
                                                  handleSingleItemToggle(
                                                    app.key,
                                                    docId,
                                                    doc.title,
                                                    doc.status,
                                                  )
                                                }
                                                className="onboarding-custom-checkbox"
                                                type="checkbox"
                                              />
                                              <span
                                                className="min-w-0 truncate"
                                                title={doc.title || doc.name}
                                              >
                                                {doc.title || doc.name}
                                              </span>
                                            </div>
                                            <div
                                              className={`flex h-full min-w-0 items-center gap-[7px] border-l border-[rgba(0,0,0,0.08)] px-[20px] ${doc.status === "In Progress" || doc.status === "Re-syncing" ? "rotating-svg" : ""}`}
                                            >
                                              {(doc.status ===
                                                "Training Complete" ||
                                                doc.status ===
                                                  "Re-syncing Complete") && (
                                                <CompleteIcon />
                                              )}
                                              {(doc.status === "In Progress" ||
                                                doc.status ===
                                                  "Re-syncing") && (
                                                <TrainingProgressIcon />
                                              )}
                                              {doc.status ===
                                                "File Rejected" && (
                                                <IErrIconSvg />
                                              )}
                                              <span className="truncate">
                                                {doc.status}
                                              </span>
                                            </div>
                                            <div className="flex h-full min-w-0 items-center border-l border-[rgba(0,0,0,0.08)] px-[20px] text-[#555]">
                                              <span className="truncate">
                                                {(
                                                  doc.characterCount || 0
                                                ).toLocaleString()}
                                              </span>
                                            </div>
                                            <div className="flex h-full min-w-0 items-center border-l border-[rgba(0,0,0,0.08)] px-[20px] text-[#555]">
                                              <span className="truncate">
                                                {moment(
                                                  doc.updatedAt ||
                                                    doc.createdAt,
                                                ).format("DD MMM, YYYY")}
                                              </span>
                                            </div>
                                            <div className="flex h-full items-center justify-center gap-[12px] border-l border-[rgba(0,0,0,0.08)] px-[20px]">
                                              <button
                                                type="button"
                                                title="Delete file"
                                                className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] text-[#fb5f66] transition-colors hover:bg-[#FFF7F7]"
                                                onClick={() =>
                                                  (() => {
                                                    setPendingDeleteConfig({
                                                      title: app.title,
                                                      sourceType: app.key,
                                                      source: [doc],
                                                    });
                                                    setRemoveData({
                                                      status: true,
                                                      remove: async () => {
                                                        await deleteTrainDocument(
                                                          {
                                                            source: [doc],
                                                            sourceType: app.key,
                                                          },
                                                        );
                                                        setPendingDeleteConfig(
                                                          null,
                                                        );
                                                        setRemoveData(
                                                          (prev) => ({
                                                            ...prev,
                                                            status: false,
                                                          }),
                                                        );
                                                      },
                                                    });
                                                  })()
                                                }
                                              >
                                                <UploadDeleteIcon />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      },
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </section>
        ))}
      </motion.div>
      {removeData.status && pendingDeleteConfig && (
        <ModalWrapper>
          <RemoveConfirmation
            removeData={removeData}
            setRemoveData={setRemoveData}
            isLoading={inProcess}
            title="Delete Trained Source"
            message={buildConnectedSourceDeleteMessage(
              pendingDeleteConfig.source,
            )}
          />
        </ModalWrapper>
      )}
    </>
  );
};

export default ConnectAppsStep;
